import { useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowUpRight, MessageCircle, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BookingBar, useIsMobile } from "@/components/booking/BookingBar";
import { RoomCard } from "@/components/rooms/RoomCard";
import { PosterCard } from "@/components/events/PosterCard";
import { MenuCard } from "@/components/dining/MenuCard";
import { Container, Reveal, SectionTitle, Img, ParallaxImg, HScroll, EASE } from "@/components/common/Primitives";
import { TodayTable, NearbyList, MeetingRow, OpenNow } from "@/components/home/HomeBlocks";
import { HeroSlides, useSlideshow, WordReveal, ExpandOnScroll } from "@/components/home/HeroKit";
import { FilmSection } from "@/components/home/FilmSection";
import { DayStory } from "@/components/home/DayStory";
import { VillageMap } from "@/components/home/VillageMap";
import { useConcierge } from "@/components/concierge/ConciergeContext";
import { useLang } from "@/lib/i18n";
import { useContent, upcomingHappenings } from "@/lib/content";
import { usePageMeta } from "@/lib/seo";
import { SIZES, srcSetFor } from "@/lib/images";
import { HOTEL } from "@/lib/hotel";
import { LogoMark } from "@/components/common/Logo";

/* ------------------------------------------------------------------ content */

/**
 * Hero photos – three real pictures that crossfade slowly on desktop, each with a short factual
 * caption. Phones get a single portrait crop of the aerial so the first paint stays light.
 */
const HERO_SLIDES = [
  { src: "/images/hotel-aerial.jpg", position: "50% 58%", nn: ["Skånevik Fjordhotel sett frå lufta, med hagen, bassenget og småbåthamna", "Hotellet frå lufta – hagen, bassenget og kaien"], en: ["Skånevik Fjordhotel from the air, with the garden, the pool and the marina", "The hotel from the air – garden, pool and quay"] },
  { src: "/images/peparen-summer.jpg", position: "50% 55%", nn: ["Badeøya Peparen ein sommardag", "Badeøya Peparen – fem minutt å gå frå hotellet"], en: ["Peparen bathing island on a summer day", "Peparen bathing island – a five-minute walk from the hotel"] },
  { src: "/images/pool-wide.jpg", position: "50% 55%", nn: ["Utandørsbassenget Sjøkanten Trivsel med fjorden bak", "Sjøkanten Trivsel – oppvarma basseng i hagen, juni til august"], en: ["The outdoor pool Sjøkanten Trivsel with the fjord behind", "Sjøkanten Trivsel – heated garden pool, June to August"] },
];
const HERO_IMG_MOBILE = "/images/hotel-aerial-portrait.jpg";
const HERO_IMG_TABLET = "/images/hotel-aerial.jpg";

const HERO_COPY = {
  kicker: ["Skånevik · mellom Preikestolen og Trolltunga", "Skånevik · between Preikestolen and Trolltunga"],
  title: ["Hotellet\nved fjorden.", "The hotel\nby the fjord."],
  sub: ["Familiedrive hotell ved kaien i Skånevik – med restaurant, Strandkafé og utandørsbasseng heilt nede ved sjøen.", "A family-run hotel on the quay in Skånevik – with a restaurant, beach café and outdoor pool right by the sea."],
  alt: ["Skånevik Fjordhotel og bygda sett frå lufta", "Skånevik Fjordhotel and the village from the air"],
};

const useUp = (reduce) => (delay = 0) => ({
  initial: reduce ? false : { opacity: 0, y: 22 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, delay, ease: EASE },
});

/**
 * Desktop (>= 1024px). One full screen of film under the transparent header. Everything that needs
 * saying sits in the lower third, left-aligned on one grid: kicker, a two-line headline, the lead,
 * the search bar and – under a hairline that fills as the loop plays – the facts and the place.
 */
const DesktopHero = () => {
  const { tx, lang } = useLang();
  const reduce = useReducedMotion();
  const up = useUp(reduce);
  const ref = useRef(null);
  const slides = useMemo(() => HERO_SLIDES.map((sl) => ({ src: sl.src, position: sl.position, alt: (lang === "en" ? sl.en : sl.nn)[0], caption: (lang === "en" ? sl.en : sl.nn)[1] })), [lang]);
  const { index } = useSlideshow(slides.length, { interval: 7000, enabled: !reduce });
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const copyY = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : 80]);
  const copyOpacity = useTransform(scrollYProgress, [0, 0.45], [1, 0]);

  return (
    <section ref={ref} className="relative h-[100svh] max-h-[1100px] min-h-[720px] overflow-hidden bg-[hsl(214,30%,12%)]" data-testid="home-hero">
      <div className="absolute inset-0" data-testid="home-hero-photo">
        <HeroSlides slides={slides} index={index} parallaxRef={ref} reduce={reduce} />
      </div>
      <div className="scrim-nav pointer-events-none absolute inset-x-0 top-0 z-[3] h-44" aria-hidden />
      <div className="pointer-events-none absolute inset-x-0 top-0 z-[3] h-36 bg-gradient-to-b from-[hsl(214_40%_8%_/_0.42)] to-transparent" aria-hidden />
      <div className="scrim-hero pointer-events-none absolute inset-x-0 bottom-0 z-[3] h-[82%]" aria-hidden />

      <Container className="relative z-[4] flex h-full flex-col justify-end pb-10 xl:pb-14">
        <motion.div style={{ y: copyY, opacity: copyOpacity }} className="grid grid-cols-12 items-end gap-x-8 gap-y-6">
          <div className="col-span-12 xl:col-span-7">
            <motion.p {...up(0.06)} className="hero-shadow flex items-center gap-3 text-[0.9375rem] font-medium text-white/85 before:h-px before:w-8 before:bg-white/70" data-testid="home-hero-kicker">{tx(...HERO_COPY.kicker)}</motion.p>
            <WordReveal
              text={tx(...HERO_COPY.title)}
              delay={0.12}
              reduce={reduce}
              className="hero-shadow mt-5 text-[clamp(3.5rem,6.4vw,7.25rem)] font-semibold leading-[0.9] tracking-[-0.045em] text-white"
              data-testid="home-hero-title"
            />
          </div>
          <motion.p {...up(0.34)} className="hero-shadow col-span-12 max-w-[36rem] text-lg leading-relaxed text-white/85 xl:col-span-5 xl:col-start-8 xl:pb-2 xl:text-xl" data-testid="home-hero-sub">{tx(...HERO_COPY.sub)}</motion.p>
        </motion.div>

        <motion.div {...up(0.3)} className="mt-10 xl:mt-12" data-sticky-anchor data-testid="home-booking-strip">
          <BookingBar variant="hero" />
        </motion.div>

      </Container>
    </section>
  );
};

/**
 * Phones + tablets (< 1024px): one full screen of film (portrait rendition on phones) under the
 * transparent header; headline and the booking card sit together at the bottom of the picture.
 */
const MobileHero = () => {
  const { tx } = useLang();
  const reduce = useReducedMotion();
  const up = useUp(reduce);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const copyOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);
  const photoY = useTransform(scrollYProgress, [0, 1], ["0%", reduce ? "0%" : "8%"]);

  return (
    <section className="relative" data-testid="home-hero">
      <div ref={ref} className="relative h-[100svh] max-h-[960px] min-h-[640px] overflow-hidden bg-[hsl(214,30%,12%)]" data-testid="home-hero-photo">
        <motion.div style={{ y: photoY }} className="absolute inset-x-0 -top-[6%] h-[112%] will-change-transform">
          <motion.picture initial={reduce ? false : { scale: 1.06 }} animate={{ scale: 1 }} transition={{ duration: 5.5, ease: [0.16, 1, 0.3, 1] }} className="block h-full w-full origin-center">
            <source media="(max-width: 639px)" type="image/webp" srcSet={srcSetFor(HERO_IMG_MOBILE)} sizes="100vw" />
            <source type="image/webp" srcSet={srcSetFor(HERO_IMG_TABLET)} sizes="100vw" />
            <img src={HERO_IMG_TABLET} alt={tx(...HERO_COPY.alt)} fetchPriority="high" decoding="async" className="h-full w-full object-cover" style={{ objectPosition: "50% 50%" }} data-testid="home-hero-image" />
          </motion.picture>
        </motion.div>
        <div className="scrim-nav pointer-events-none absolute inset-x-0 top-0 h-32" aria-hidden />
        <div className="pointer-events-none absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-[hsl(214_40%_8%_/_0.5)] to-transparent" aria-hidden />
        <div className="scrim-hero-strong pointer-events-none absolute inset-x-0 bottom-0 h-[78%]" aria-hidden />
        <Container className="relative flex h-full flex-col justify-end pb-5 sm:pb-8">
          <motion.div style={{ opacity: copyOpacity }}>
            <motion.p {...up(0.06)} className="hero-shadow flex items-center gap-3 text-sm font-medium text-white/85 before:h-px before:w-6 before:bg-white/70" data-testid="home-hero-kicker">{tx(...HERO_COPY.kicker)}</motion.p>
            <WordReveal text={tx(...HERO_COPY.title)} delay={0.1} reduce={reduce} className="hero-shadow mt-3 text-[3rem] font-semibold leading-[0.92] tracking-[-0.045em] text-white sm:text-[4rem]" data-testid="home-hero-title" />
            <motion.p {...up(0.18)} className="hero-shadow mt-4 hidden max-w-md text-[1.0625rem] leading-relaxed text-white/90 sm:block sm:text-lg" data-testid="home-hero-sub">{tx(...HERO_COPY.sub)}</motion.p>
          </motion.div>
          <motion.div {...up(0.26)} className="mt-6 w-full sm:mt-8 sm:max-w-[640px]" data-sticky-anchor data-testid="home-booking-strip">
            <BookingBar variant="hero" />
          </motion.div>
        </Container>
      </div>
    </section>
  );
};

const Hero = () => (useIsMobile() ? <MobileHero /> : <DesktopHero />);

/* ------------------------------------------------------------------ page */

export default function Home() {
  const { lang, tx, tr } = useLang();
  const { data } = useContent();
  const { ask } = useConcierge();
  const phone = useIsMobile(768);
  usePageMeta(
    tx("Skånevik Fjordhotel – hotellet ved fjorden", "Skånevik Fjordhotel – the hotel by the fjord"),
    tx("Familiedrive hotell ved kaien i Skånevik, mellom Preikestolen og Trolltunga. 51 rom, restaurant, Strandkafé med steinomnspizza og utandørsbasseng. Bestill direkte.", "Family-run hotel on the quay in Skånevik, between Preikestolen and Trolltunga. 51 rooms, restaurant, beach café with stone-oven pizza and outdoor pool. Book direct.")
  );
  const roomTypes = data?.room_types || [];
  const homeRooms = roomTypes; // all five, single room last
  const activities = [...(data?.activities || [])].sort((a, b) => (a.sort || 0) - (b.sort || 0));
  const happenings = upcomingHappenings(data?.happenings).slice(0, 4);
  const meetingRooms = data?.meeting_rooms || [];
  const pizzaMenu = (data?.dining?.menus || []).find((m) => m.id === "pizza");
  const komle = (data?.dining?.offers || []).find((o) => o.id === "komle");

  const questions = lang === "en"
    ? ["Is the pool open in September?", "Which room suits a family of four?", "Can I bring my dog?", "How do I get here from Bergen?"]
    : ["Er bassenget ope i september?", "Kva rom passar for ein familie på fire?", "Kan eg ta med hund?", "Korleis kjem eg hit frå Bergen?"];

  return (
    <div data-testid="home-page">
      <Hero />

      {/* Intro: statement + today card */}
      <section className="pt-20 lg:pt-28" data-testid="home-intro">
        <Container>
          <Reveal>
            <p className="flex items-center gap-3 text-sm font-semibold text-muted-foreground before:h-px before:w-8 before:bg-border">{tx("Skånevik Fjordhotel · Sunnhordland", "Skånevik Fjordhotel · Sunnhordland")}</p>
            <div className="mt-6 grid grid-cols-12 items-end gap-x-8 gap-y-8">
              <p className="col-span-12 max-w-[20ch] text-[2.125rem] font-semibold leading-[1.02] tracking-[-0.035em] text-foreground sm:text-[2.75rem] lg:col-span-8 lg:text-[3.25rem]" data-testid="home-statement-text">
                {tx("Frå frukostbordet ser du småbåthamna, badeøya Peparen og fjellsidene på andre sida av fjorden.", "From the breakfast table you see the marina, Peparen island and the mountainsides across the fjord.")}
              </p>
              <div className="col-span-12 lg:col-span-4 lg:pb-2">
                <p className="max-w-[44ch] text-[1.0625rem] leading-relaxed text-muted-foreground lg:text-lg">
                  {tx("Hotellet ligg ved kaien i Skånevik, ei bygd med om lag 1 000 innbyggjarar inst i Skånevikfjorden. Det meste du treng, ligg innanfor fem minutt til fots.", "The hotel sits on the quay in Skånevik, a village of around 1,000 people at the head of the Skånevik fjord. Most of what you need is within a five-minute walk.")}
                </p>
                <Link to="/om-oss" className="mt-5 inline-flex items-center gap-1.5 text-[0.9375rem] font-semibold text-foreground underline decoration-border underline-offset-[6px] transition-colors hover:decoration-foreground" data-testid="home-intro-about-link">{tx("Om hotellet og bygda", "About the hotel and the village")} <ArrowRight className="h-4 w-4" /></Link>
              </div>
            </div>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 gap-12 lg:mt-20 lg:grid-cols-12 lg:gap-x-16">
            <Reveal className="lg:col-span-7"><TodayTable dining={data?.dining} /></Reveal>
            <Reveal delay={0.06} className="lg:col-span-5"><NearbyList /></Reveal>
          </div>
        </Container>
      </section>

      {/* The hotel's own film, shown whole */}
      <FilmSection />

      {/* Rooms */}
      <section className="pt-20 lg:pt-32" data-testid="home-rooms">
        <Container>
          <SectionTitle
            size="lg"
            index="01"
            eyebrow={tx("Rom", "Rooms")}
            title={tx("Finn rommet ditt", "Find your room")}
            sub={tx("Fem romtypar i same hus – frå einerom til fjordsuite. Frukost er inkludert, og du betaler når du kjem.", "Five room types under one roof – from single rooms to the fjord suite. Breakfast is included, and you pay when you arrive.")}
            action={tx("Sjå alle rom", "See all rooms")}
            to="/rom"
            testid="home-rooms-title"
          />
          <HScroll
            className="mt-8 lg:mt-10"
            testid="home-rooms-scroller"
            items={homeRooms.map((rt) => ({ ...rt, w: "w-[78vw] max-w-[340px] sm:w-[46vw] sm:max-w-none lg:w-[392px]" }))}
            renderItem={(rt, i) => <RoomCard rt={rt} testid={`home-room-card-${rt.id}`} eager={i < 3} aspect="aspect-[4/3]" />}
          />
          <Reveal className="mt-6 flex flex-wrap items-center justify-between gap-x-8 gap-y-3 lg:mt-8">
            <p className="text-sm text-muted-foreground">{tx("Prisane er per rom per natt med frukost. Gratis avbestilling fram til kl. 18.00 dagen før.", "Prices are per room per night with breakfast. Free cancellation until 18:00 the day before.")}</p>
            <Link to="/rom" className="inline-flex items-center gap-1.5 text-[0.9375rem] font-semibold text-foreground underline decoration-border underline-offset-[6px] transition-colors hover:decoration-foreground" data-testid="home-rooms-compare-link">{tx("Samanlikn alle fem romtypane", "Compare all five room types")} <ArrowRight className="h-4 w-4" /></Link>
          </Reveal>
        </Container>
      </section>

      {/* A day by the fjord – pinned photo + hours */}
      <DayStory />

      {/* The village – interactive illustrated map */}
      <VillageMap />

      {/* Summer band – Peparen */}
      <ExpandOnScroll className="relative h-[78svh] max-h-[860px] min-h-[520px]" radius={20} testid="home-summer-band">
        <section className="relative h-full w-full overflow-hidden bg-primary">
        <ParallaxImg src="/images/peparen-beach.jpg" mobileSrc="/images/peparen-bridge.jpg" alt={tx("Badeøya Peparen ein sommardag", "Peparen bathing island on a summer day")} className="absolute inset-0 h-full w-full" radius="rounded-none" position="50% 60%" mobilePosition="50% 60%" range={7} sizes={SIZES.full} />
        <div className="scrim-hero-soft pointer-events-none absolute inset-x-0 bottom-0 h-[70%]" aria-hidden />
        <Container className="relative flex h-full flex-col justify-end pb-10 lg:pb-16">
          <Reveal className="max-w-3xl">
            <p className="hero-shadow flex items-center gap-3 text-[0.9375rem] font-semibold text-white before:h-px before:w-8 before:bg-white/70">{tx("Sommar i Skånevik", "Summer in Skånevik")}</p>
            <h2 className="hero-shadow mt-4 max-w-[16ch] text-[2.5rem] font-semibold leading-[0.95] tracking-[-0.04em] text-white sm:text-[3.25rem] lg:text-[4rem]">{tx("Bada der bygda badar", "Swim where the village swims")}</h2>
            <p className="hero-shadow mt-4 max-w-lg text-[1.0625rem] leading-relaxed text-white/90 sm:text-lg">
              {tx("Fem minutt frå hotellet ligg badeøya Peparen med sandstrand, svaberg og stupebrett. Heime i hagen ventar det oppvarma bassenget Sjøkanten Trivsel.", "Five minutes from the hotel lies Peparen with its sandy beach, smooth rocks and diving board. Back in the garden, the heated pool Sjøkanten Trivsel awaits.")}
            </p>
            <div className="mt-7 flex flex-wrap items-center gap-3 lg:mt-8">
              <Button asChild size="lg" variant="inverse" data-testid="home-summer-cta">
                <Link to="/opplev#peparen">{tx("Sjå kva du kan oppleve", "See what to experience")} <ArrowRight className="h-4 w-4" /></Link>
              </Button>
            </div>
          </Reveal>
        </Container>
        </section>
      </ExpandOnScroll>

      {/* Explore rail */}
      <section className="pt-20 lg:pt-32" data-testid="home-explore">
        <Container>
          <SectionTitle
            size="lg"
            index="04"
            eyebrow={tx("Opplev", "Explore")}
            title={tx("Dagane dine i Skånevik", "Your days in Skånevik")}
            sub={tx("Frå bassenget i hagen til Trolltunga. Det meste startar ved hotelldøra – kajakk, sykkel og turkart får du i resepsjonen.", "From the pool in the garden to Trolltunga. Most of it starts at the hotel door – kayaks, bikes and trail maps are at reception.")}
            action={tx("Alt du kan oppleve", "Everything to experience")}
            to="/opplev"
            testid="home-explore-title"
          />
          <HScroll
            className="mt-8 lg:mt-10"
            testid="home-explore-rail"
            items={activities.map((a) => ({ ...a, w: "w-[72vw] sm:w-[40vw] lg:w-[300px]" }))}
            renderItem={(a, i) => (
              <Link to={`/opplev#${a.id}`} className="group block rounded-[22px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-4" data-testid={`home-explore-${a.id}`}>
                <Img src={a.images?.[0]} alt={tr(a.title)} className="img-zoom aspect-[4/5]" radius="rounded-[22px]" sizes={SIZES.quarter} position={a.id === "pool" ? "50% 55%" : a.id === "peparen" ? "50% 60%" : "50% 50%"} eager={i < 2} />
                <div className="mt-4 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-[1.0625rem] font-semibold leading-tight text-foreground">{tr(a.title)}</p>
                    <p className="mt-1 text-sm leading-snug text-muted-foreground">{tr(a.kicker)}</p>
                  </div>
                  <ArrowUpRight className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-foreground" />
                </div>
              </Link>
            )}
          />
        </Container>
      </section>

      {/* Food – photo first, the pizza menu beside it, komle Thursday as a slim row */}
      <section className="mt-20 bg-secondary/60 py-20 lg:mt-32 lg:py-32" data-testid="home-dining">
        <Container>
          <Reveal>
            <div className="grid grid-cols-12 items-end gap-x-8 gap-y-6">
              <div className="col-span-12 lg:col-span-8">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                  <p className="flex items-center gap-3 text-xs font-medium tracking-wide text-muted-foreground"><span className="tabular text-foreground">05</span><span className="h-px w-8 bg-border" aria-hidden />{tx("Mat", "Dining")}</p>
                  <OpenNow dining={data?.dining} />
                </div>
                <h2 className="mt-4 max-w-[18ch] text-[2.125rem] font-semibold leading-[0.98] tracking-[-0.035em] text-foreground sm:text-[2.75rem] lg:text-[3.25rem]">{tx("Frukost med utsikt. Pizza på kaien.", "Breakfast with a view. Pizza on the quay.")}</h2>
              </div>
              <div className="col-span-12 lg:col-span-4 lg:pb-2">
                <p className="max-w-[44ch] text-[1.0625rem] leading-relaxed text-muted-foreground lg:text-lg">{tx("Restauranten serverer frukost, middag og søndagsmiddag med fjorden rett utanfor vindauget. Nede på kaien ligg Strandkaféen med steinomn, kaffi og is.", "The restaurant serves breakfast, dinner and Sunday dinner with the fjord right outside the window. Down on the quay, Strandkaféen has a stone oven, coffee and ice cream.")}</p>
                <div className="mt-5 flex flex-wrap gap-3">
                  <Button asChild size="lg" data-testid="home-dining-cta"><Link to="/mat">{tx("Menyar og opningstider", "Menus and opening hours")} <ArrowRight className="h-4 w-4" /></Link></Button>
                  <Button asChild variant="soft" size="lg" data-testid="home-book-table-cta"><Link to="/mat#bord">{tx("Bestill bord", "Book a table")}</Link></Button>
                </div>
              </div>
            </div>
          </Reveal>

          <div className="mt-10 grid grid-cols-1 gap-6 lg:mt-14 lg:grid-cols-12 lg:items-stretch lg:gap-8">
            <Reveal className="lg:col-span-7">
              <Link to="/mat" className="group relative block h-full overflow-hidden rounded-[22px] bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-4" data-testid="home-dining-photo">
                <Img src="/images/restaurant-window.jpg" alt={tx("Dekka bord ved vindauget i restauranten, med fjorden utanfor", "A laid table by the restaurant window, the fjord outside")} className="img-zoom aspect-[4/3] h-full w-full lg:aspect-auto lg:absolute lg:inset-0" radius="rounded-none" sizes={SIZES.half} position="50% 55%" />
                <div className="scrim-hero-soft pointer-events-none absolute inset-x-0 bottom-0 h-1/2" aria-hidden />
                <div className="hero-shadow absolute inset-x-6 bottom-6 flex items-end justify-between gap-4 text-white lg:inset-x-8 lg:bottom-8">
                  <div>
                    <p className="text-[1.125rem] font-semibold leading-tight lg:text-[1.25rem]">{tx("Restauranten", "The restaurant")}</p>
                    <p className="mt-1 text-sm text-white/80 tabular">{tx("Frukost 07.30–10.00 · middag 17.00–21.00 · søndagsmiddag 13.00–18.00", "Breakfast 07:30–10:00 · dinner 17:00–21:00 · Sunday dinner 13:00–18:00")}</p>
                  </div>
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/15 backdrop-blur-md transition-colors group-hover:bg-white group-hover:text-foreground"><ArrowUpRight className="h-4 w-4" /></span>
                </div>
              </Link>
            </Reveal>
            <Reveal delay={0.06} className="lg:col-span-5">
              {pizzaMenu ? <MenuCard menu={pizzaMenu} testid="home-pizza-menu" /> : <Img src="/images/cafe-chalk-counter.jpg" alt="" className="aspect-[4/5]" radius="rounded-[22px]" sizes={SIZES.half} />}
            </Reveal>
          </div>

          {komle && (
            <Reveal delay={0.08} className="mt-6 lg:mt-8">
              <Link to="/mat#komle" className="group flex flex-col gap-5 rounded-[22px] border border-border bg-card p-4 transition-colors hover:bg-card/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:flex-row sm:items-center sm:gap-7 sm:p-5" data-testid="home-dining-komle">
                <Img src="/images/flyer-komle.jpg" alt={tr(komle.title)} className="aspect-[1491/1055] w-full shrink-0 sm:w-44 lg:w-56" radius="rounded-xl" sizes={SIZES.quarter} reveal={false} />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-muted-foreground">{tr(komle.when)}</p>
                  <h3 className="mt-1 text-[1.375rem] font-semibold leading-tight tracking-[-0.02em] text-foreground">{tr(komle.title)}</h3>
                  <p className="mt-2 max-w-[60ch] text-[0.9375rem] leading-relaxed text-muted-foreground">{tr(komle.text)}</p>
                </div>
                <span className="hidden h-11 w-11 shrink-0 items-center justify-center rounded-full border border-border text-foreground transition-colors group-hover:bg-foreground group-hover:text-background sm:flex"><ArrowUpRight className="h-4 w-4" /></span>
              </Link>
            </Reveal>
          )}
        </Container>
      </section>

      {/* What's on */}
      {happenings.length > 0 && (
        <section className="py-20 lg:py-32" data-testid="home-whats-on">
          <Container>
            <SectionTitle size="lg" index="06" eyebrow={tx("Det skjer", "What's on")} title={tx("Det skjer på Fjordhotellet", "What's on at the Fjord Hotel")} sub={tx("Foredrag, konsertar, julebord og faste kveldar på Strandkaféen.", "Talks, concerts, Christmas dinners and regular nights at Strandkaféen.")} action={tx("Sjå alt som skjer", "See everything on")} to="/arrangement#det-skjer" testid="home-whats-on-title" />
            {phone ? (
              <HScroll className="mt-8" testid="home-posters-scroller" items={happenings.map((h) => ({ ...h, w: "w-[70vw] max-w-[300px]" }))} renderItem={(h) => <PosterCard item={h} testid={`home-poster-${h.id}`} />} />
            ) : (
              <div className="mt-10 grid grid-cols-2 gap-6 lg:grid-cols-4" data-testid="home-posters-grid">
                {happenings.map((h, i) => <Reveal key={h.id} delay={i * 0.05}><PosterCard item={h} testid={`home-poster-${h.id}`} /></Reveal>)}
              </div>
            )}
          </Container>
        </section>
      )}

      {/* Meetings & celebrations – one calm band */}
      {meetingRooms.length > 0 && (
        <section className="bg-secondary/70 py-20 lg:py-32" data-testid="home-meetings">
          <Container className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:items-center lg:gap-16">
            <Reveal className="lg:col-span-7">
              <div className="grid grid-cols-5 gap-3 sm:gap-4">
                <Img src="/images/meeting-skaano.jpg" alt={tx("Konferansesalen Skåno", "The Skåno conference hall")} className="img-zoom col-span-3 aspect-[4/5]" radius="rounded-[22px]" sizes={SIZES.half} position="50% 50%" />
                <div className="col-span-2 flex flex-col gap-3 pt-10 sm:gap-4 sm:pt-14">
                  <Img src="/images/restaurant-hall.jpg" alt={tx("Restauranten dekka til fest", "The restaurant set for a celebration")} className="img-zoom aspect-[3/4]" radius="rounded-[18px]" sizes={SIZES.quarter} position="40% 60%" />
                  <Img src="/images/meeting-peparen.jpg" alt={tx("Møterommet Peparen", "The Peparen meeting room")} className="img-zoom aspect-square" radius="rounded-[18px]" sizes={SIZES.quarter} position="50% 50%" />
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.06} className="lg:col-span-5">
              <p className="flex items-center gap-3 text-xs font-medium tracking-wide text-muted-foreground"><span className="tabular text-foreground">07</span><span className="h-px w-8 bg-border" aria-hidden />{tx("Selskap og møte", "Celebrations and meetings")}</p>
              <h2 className="mt-4 text-[2rem] leading-[1.02] tracking-[-0.03em] text-foreground sm:text-[2.5rem]">{tx("Tre møterom, ein festsal – og rom til alle", "Three meeting rooms, one banquet hall – and rooms for everyone")}</h2>
              <p className="mt-4 max-w-[48ch] text-base leading-relaxed text-muted-foreground sm:text-lg">{tx("Frå styremøte for ti til bryllaup for nitti. Dagpakke med lunsj og kaffi, selskapsmeny frå kjøkenet, og 51 rom i same hus.", "From a board meeting for ten to a wedding for ninety. Day package with lunch and coffee, a party menu from the kitchen, and 51 rooms under the same roof.")}</p>
              <div className="mt-7 border-b border-border" data-testid="home-meeting-rows">
                {meetingRooms.map((m) => <MeetingRow key={m.id} m={m} tr={tr} tx={tx} />)}
              </div>
              <div className="mt-7 flex flex-wrap gap-3">
                <Button asChild size="lg" data-testid="home-meetings-cta"><Link to="/arrangement">{tx("Sjå lokale og kapasitet", "See venues and capacity")} <ArrowRight className="h-4 w-4" /></Link></Button>
                <Button asChild variant="soft" size="lg" data-testid="home-meetings-enquiry"><Link to="/arrangement#forespurnad">{tx("Send førespurnad", "Send an enquiry")}</Link></Button>
              </div>
            </Reveal>
          </Container>
        </section>
      )}

      {/* Fjordverten – ink panel */}
      <section className="py-20 lg:py-32" data-testid="home-concierge">
        <Container>
          <Reveal className="grain relative overflow-hidden rounded-2xl bg-primary px-6 py-10 text-primary-foreground sm:px-10 sm:py-12 lg:px-14 lg:py-16" data-testid="home-fjordverten-panel">
            <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-12">
              <div className="lg:col-span-6">
                <p className="text-sm font-semibold text-white/60">{tx("Fjordverten · svarar døgnet rundt", "Fjordverten · answers around the clock")}</p>
                <h2 className="mt-3 text-[2rem] leading-[1.0] tracking-[-0.035em] text-white sm:text-[2.5rem] lg:text-[3rem]">{tx("Spør oss om opphaldet", "Ask us about your stay")}</h2>
                <p className="mt-4 max-w-[48ch] text-[1.0625rem] leading-relaxed text-white/70 sm:text-lg">{tx(`Fjordverten er den digitale resepsjonisten vår. Han svarar på spørsmål om rom, mat, bassenget og vegen hit – og kan lage eit prisoverslag. Du kan også ringje oss på ${HOTEL.phoneDisplay}.`, `Fjordverten is our digital receptionist. It answers questions about rooms, food, the pool and how to get here – and can prepare a price estimate. You can also call us on ${HOTEL.phone}.`)}</p>
                <div className="mt-7 flex flex-wrap gap-3">
                  <Button size="lg" variant="inverse" onClick={() => ask(null)} data-testid="home-concierge-open"><MessageCircle className="h-4 w-4" /> {tx("Spør Fjordverten", "Ask Fjordverten")}</Button>
                  <Button asChild size="lg" variant="ghost-light" className="border border-white/20 tabular" data-testid="home-call-cta"><a href={HOTEL.tel}><Phone className="h-4 w-4" /> {HOTEL.phoneDisplay}</a></Button>
                </div>
              </div>
              <div className="lg:col-span-5 lg:col-start-8">
                {/* A real exchange, so the panel shows what Fjordverten actually does */}
                <div className="space-y-3" data-testid="home-concierge-sample" aria-hidden>
                  <div className="flex justify-end">
                    <p className="max-w-[85%] rounded-2xl rounded-br-md bg-white px-4 py-2.5 text-sm font-medium leading-snug text-foreground">{tx("Er bassenget ope i september?", "Is the pool open in September?")}</p>
                  </div>
                  <div className="flex items-end gap-2.5">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white/10 text-white"><LogoMark className="h-3.5" /></span>
                    <p className="max-w-[88%] rounded-2xl rounded-bl-md border border-white/15 bg-white/[0.06] px-4 py-2.5 text-sm leading-snug text-white/90">{tx("Nei – Sjøkanten Trivsel er ope frå juni til august. Badeøya Peparen ligg fem minutt unna og er open heile året, og i september er sjøen framleis rundt 15 grader.", "No – Sjøkanten Trivsel is open from June to August. Peparen bathing island is a five-minute walk away and open all year, and in September the sea is still around 15 degrees.")}</p>
                  </div>
                </div>
                <p className="mt-7 text-sm font-semibold text-white/60">{tx("Forslag til spørsmål", "Suggested questions")}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {questions.map((q, i) => (
                    <button key={q} type="button" onClick={() => ask(q)} data-testid={`home-concierge-chip-${i}`} className="rounded-md border border-white/15 bg-white/[0.06] px-3.5 py-2 text-left text-sm font-medium text-white transition-colors hover:bg-white/[0.14] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60">{q}</button>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        </Container>
      </section>
    </div>
  );
}
